import {
  GenericEscposPrinterConfig,
  GenericEscposPrintersConfig,
  PrinterDuty,
  PrinterTemplate,
} from '../types';
import { normalizePaperWidth } from '../paperWidth';
import { Capacitor } from '@capacitor/core';
import { EscPosPrinter } from '@/plugins/escpos-printer';
import { encodeTemplateToEscPos, uint8ToBase64 } from '../escpos/encodeTemplateToEscPos';
import { readPrinterSetting, writePrinterSetting } from '../printerSettingsStorage';

const normalizeGenericEscposPrinterConfig = (
  printer: Partial<GenericEscposPrinterConfig>
): GenericEscposPrinterConfig => {
  return {
    id: printer.id ?? crypto.randomUUID(),
    name: printer.name ?? 'Generic ESC/POS',
    duties: printer.duties ?? ['cashier'],
    host: printer.host ?? '',
    port: printer.port ?? 9100,
    timeout: printer.timeout ?? 3000,
    codePage: printer.codePage ?? 'cp858',
    paperWidth: normalizePaperWidth(printer.paperWidth),
    openCashDrawer: printer.openCashDrawer ?? false,
    printDispatchCopy: printer.printDispatchCopy ?? false,
    language: printer.language ?? 'es',
    currency: printer.currency ?? 'USD',
  };
};

export const createDefaultGenericEscposPrinterConfig = (
  overrides: Partial<GenericEscposPrinterConfig> = {}
): GenericEscposPrinterConfig => {
  return normalizeGenericEscposPrinterConfig({
    id: crypto.randomUUID(),
    duties: ['cashier'],
    ...overrides,
  });
};

export const getGenericEscposPrintersConfig = (): GenericEscposPrintersConfig => {
  try {
    const dbConfig = readPrinterSetting<GenericEscposPrintersConfig>('genericEscposPrintersConfig');
    if (dbConfig) {
      return {
        enabled: dbConfig.enabled ?? false,
        printers: (dbConfig.printers ?? []).map(normalizeGenericEscposPrinterConfig),
      };
    }
  } catch (error) {
    console.warn('Failed to parse generic ESC/POS config:', error);
  }

  return { enabled: false, printers: [] };
};

export const saveGenericEscposPrintersConfig = async (
  config: GenericEscposPrintersConfig
): Promise<void> => {
  await writePrinterSetting('genericEscposPrintersConfig', config);
};

const isNativeEscPosApp = (): boolean => {
  if (!Capacitor.isNativePlatform()) return false;
  const platform = Capacitor.getPlatform();
  return platform === 'android' || platform === 'ios';
};

const getPrintersForDuty = (duty: PrinterDuty): GenericEscposPrinterConfig[] => {
  const config = getGenericEscposPrintersConfig();
  if (!config.enabled || config.printers.length === 0) return [];
  return config.printers.filter((printer) => printer.duties.includes(duty));
};

export const sendToGenericEscposPrinter = async (
  template: PrinterTemplate,
  printerId: string,
  options?: { openCashDrawer?: boolean },
): Promise<boolean> => {
  const config = getGenericEscposPrintersConfig();
  if (!config.enabled) return false;

  const printer = config.printers.find((item) => item.id === printerId);
  if (!printer) {
    throw new Error(`Generic ESC/POS printer not found: ${printerId}`);
  }
  const bytes = encodeTemplateToEscPos(template, {
    openCashDrawer: options?.openCashDrawer ?? printer.openCashDrawer,
    codePage: printer.codePage,
  });

  if (isNativeEscPosApp()) {
    const dataBase64 = uint8ToBase64(bytes);
    await EscPosPrinter.printRaw({
      host: printer.host,
      port: printer.port,
      timeoutMs: printer.timeout,
      dataBase64,
    });
    return true;
  }

  if (!window.electronAPI?.printEscposTcp) {
    throw new Error('Generic ESC/POS printing is available only in the Android, iOS, or Electron app');
  }

  return window.electronAPI.printEscposTcp({
    host: printer.host,
    port: printer.port,
    timeout: printer.timeout,
    payload: Array.from(bytes),
  });
};

export const sendToGenericEscposPrinterByDuty = async (
  template: PrinterTemplate,
  duty: PrinterDuty
): Promise<boolean> => {
  const printers = getPrintersForDuty(duty);
  if (printers.length === 0) {
    console.warn(`No generic ESC/POS printer assigned for duty: ${duty}`);
    return false;
  }

  const results = await Promise.all(
    printers.map((printer) => {
      console.log(`[Generic ESC/POS] Enviando documento a la impresora: ${printer.name} (Responsabilidad: ${duty})`);
      return sendToGenericEscposPrinter(template, printer.id);
    })
  );

  return results.some((success) => success);
};
